import { useEffect, useState } from 'react';
import pjson from '../../../package.json';
import { useCurrentUser } from '../../providers/UserProvider';
import { useUserIndexedDB } from './userMutationIndexedDB';

export const useOnlineStatus = () => {
  const getIsOnline = () => {
    if (typeof navigator !== 'undefined') {
      const connection =
        navigator.connection ||
        navigator.mozConnection ||
        navigator.webkitConnection;
      if (connection && connection.effectiveType) {
        if (
          connection.effectiveType === '2g' ||
          connection.effectiveType === 'slow-2g'
        ) {
          return false;
        }
      }
      return navigator.onLine;
    }
    return true;
  };

  const [onlineStatus, setOnlineStatus] = useState(getIsOnline);

  useEffect(() => {
    const updateStatus = () => setOnlineStatus(getIsOnline());

    window.addEventListener('offline', updateStatus);
    window.addEventListener('online', updateStatus);

    const connection =
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection;
    if (connection && connection.addEventListener) {
      connection.addEventListener('change', updateStatus);
    }

    return () => {
      window.removeEventListener('offline', updateStatus);
      window.removeEventListener('online', updateStatus);
      if (connection && connection.removeEventListener) {
        connection.removeEventListener('change', updateStatus);
      }
    };
  }, []);

  return onlineStatus;
};
export const useCachedMutations = () => {
  const packageVersion = pjson.version.split('.');
  const { addMutationToQueue } = useUserIndexedDB(
    `userSpecificMutation-${packageVersion[0]}.${packageVersion[1]}`,
  );
  const { user: currentUser } = useCurrentUser(); // Assume this is a hook/function that returns the current user

  const addMutation = async (
    document,
    options,
    id,
    mock,
    refetchQueries = [],
    newIdPath,
    hasMultipleItemIds,
  ) => {
    const mutationData = {
      document,
      options,
      id,
      mock,
      refetchQueries,
      newIdPath,
      hasMultipleItemIds,
    };
    if (currentUser) {
      await addMutationToQueue(currentUser.id, mutationData);
    } else {
      throw new Error('Current user is not found');
    }
  };

  return { addMutation };
};

export const removeFunctionsFromOptions = (options) => {
  if (Array.isArray(options)) {
    return options.map(removeFunctionsFromOptions);
  }

  if (options !== null && typeof options === 'object') {
    const cleaned = {};
    for (const [key, value] of Object.entries(options)) {
      if (typeof value === 'function') continue;
      cleaned[key] = removeFunctionsFromOptions(value);
    }
    return cleaned;
  }
  return options;
};
